import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Badge } from '@/shared/ui/badge';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Pencil, Trash2, AlertCircle } from 'lucide-react';
import type { PackPreset } from '../types';
import { useTranslation } from '@/shared/i18n';
import { formatCoverUrl } from '@/shared/lib/utils';

interface PresetsListProps {
  presets: PackPreset[];
  onAddPreset: () => void;
  onRemovePreset: (preset: PackPreset) => void;
  onEditPreset: (preset: PackPreset) => void;
  canAddPreset?: boolean;
  validationErrors?: Record<string, string>;
  readOnly?: boolean;
}

export const PresetsList = ({
  presets,
  onAddPreset,
  onRemovePreset,
  onEditPreset,
  canAddPreset = true,
  validationErrors = {},
  readOnly = false,
}: PresetsListProps) => {
  const { t } = useTranslation('packs');
  const canManagePresets = canAddPreset && !readOnly;
  return (
    <div className="space-y-4">
      {/* Exibir erros de validação dos presets */}
      {validationErrors.presets && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{validationErrors.presets}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-white text-xl font-semibold">{t('presets')}</Label>
          {presets.length > 0 && (
            <Badge
              variant="outline"
              className="text-white border-white/20 bg-white/5"
            >
              {presets.length}
            </Badge>
          )}
        </div>
        {canManagePresets ? (
          <Button
            type="button"
            onClick={onAddPreset}
            className="bg-[#D91657] hover:bg-[#D91657]/90"
          >
            {t('addPreset')}
          </Button>
        ) : (
          <div className="flex flex-col items-end">
            <Button
              type="button"
              disabled
              className="bg-[#D91657] opacity-60 cursor-not-allowed"
            >
              {t('addPreset')}
            </Button>
            <span className="text-xs text-white/60 mt-1 max-w-[180px] text-right">
              {readOnly
                ? t('noPermissionToEditPresets')
                : t('addPresetsAfterCreation')}
            </span>
          </div>
        )}
      </div>

      {presets.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {presets.map((preset) => (
            <div key={preset.id} className="group relative w-48 rounded-2xl overflow-hidden shadow-lg transition-all duration-200 hover:scale-[1.02]">
              <div className="relative aspect-[4/5] w-full">
                <img
                  src={formatCoverUrl(preset.cover)}
                  alt={preset.name}
                  className="w-full h-full object-cover rounded-t-2xl"
                />

                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-t-2xl" />

                {!readOnly && (
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <Button
                      type="button"
                      title={t('editPresetAction')}
                      size="icon"
                      variant="ghost"
                      className="text-white hover:bg-white/10 hover:text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditPreset(preset);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      title={t('removePresetAction')}
                      size="icon"
                      variant="ghost"
                      className="text-white hover:bg-red-500/30 hover:text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemovePreset(preset);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              <div className="bg-zinc-900 text-white px-3 py-2 text-sm font-medium rounded-b-2xl border-t border-white/10">
                {preset.name}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-white/60 text-center py-8 border border-dashed border-white/10 rounded-lg">
          {t('noPresetsAdded')}
        </div>
      )
      }
    </div >
  );
};
